
import { User } from '../page';

interface UserDetailProps {
  user: User;
  onEdit: () => void;
  onBack: () => void;
  onToggleStatus: (userId: string) => void;
}

export default function UserDetail({ user, onEdit, onBack, onToggleStatus }: UserDetailProps) {
  const getRoleName = (role: string) => {
    return role === 'manager' ? "Navik's Manager" : "Navik's Partner";
  };

  const getRoleColor = (role: string) => {
    return role === 'manager' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800';
  };

  const getStatusColor = (isActive: boolean) => {
    return isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800';
  };

  const handleToggleStatus = () => {
    const action = user.isActive ? 'tạm khóa' : 'kích hoạt';
    if (window.confirm(`Bạn có chắc chắn muốn ${action} tài khoản "${user.displayName}"?`)) {
      onToggleStatus(user.id);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={onBack}
            className="text-gray-500 hover:text-gray-700 p-2"
          >
            <i className="ri-arrow-left-line text-xl"></i>
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Chi tiết người dùng</h1>
            <p className="text-gray-600 mt-1">Thông tin chi tiết tài khoản</p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={handleToggleStatus}
            className={`px-4 py-2 rounded-lg transition-colors whitespace-nowrap ${
              user.isActive 
                ? 'bg-orange-100 text-orange-700 hover:bg-orange-200' 
                : 'bg-green-100 text-green-700 hover:bg-green-200'
            }`}
          >
            <i className={`${user.isActive ? 'ri-lock-line' : 'ri-lock-unlock-line'} mr-2`}></i>
            {user.isActive ? 'Tạm khóa' : 'Kích hoạt'}
          </button>
          <button
            onClick={onEdit}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap"
          >
            <i className="ri-edit-line mr-2"></i>
            Chỉnh sửa
          </button>
        </div>
      </div>

      {/* User Info Card */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="bg-gradient-to-r from-blue-500 to-purple-600 px-6 py-8">
          <div className="flex items-center space-x-4">
            <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center">
              <i className="ri-user-line text-3xl text-white"></i>
            </div>
            <div className="text-white">
              <h2 className="text-2xl font-bold">{user.displayName}</h2>
              <p className="text-blue-100 mt-1">ID: {user.id}</p>
              <div className="flex items-center space-x-4 mt-3">
                <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${getRoleColor(user.role)} bg-white/90`}>
                  {getRoleName(user.role)}
                </span>
                <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${getStatusColor(user.isActive)} bg-white/90`}>
                  {user.isActive ? 'Đang hoạt động' : 'Tạm khóa'}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Thông tin liên hệ */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900 border-b border-gray-200 pb-2">
                Thông tin liên hệ
              </h3>
              
              <div className="space-y-3">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                    <i className="ri-mail-line text-blue-600"></i>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Email</p>
                    <p className="text-gray-900 font-medium">{user.email}</p>
                  </div>
                </div>

                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                    <i className="ri-phone-line text-green-600"></i>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Số điện thoại</p>
                    <p className="text-gray-900 font-medium">{user.phone}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Thông tin tài khoản */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900 border-b border-gray-200 pb-2">
                Thông tin tài khoản
              </h3>
              
              <div className="space-y-3">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                    <i className="ri-user-settings-line text-purple-600"></i>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Vai trò</p>
                    <p className="text-gray-900 font-medium">{getRoleName(user.role)}</p>
                  </div>
                </div>

                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-yellow-100 rounded-lg flex items-center justify-center">
                    <i className="ri-percent-line text-yellow-600"></i>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Tỷ lệ hoa hồng</p>
                    <p className="text-gray-900 font-medium">{user.commissionRate}%</p>
                  </div>
                </div>

                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
                    <i className="ri-calendar-line text-gray-600"></i>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Ngày tạo</p>
                    <p className="text-gray-900 font-medium">{user.createdAt}</p>
                  </div>
                </div>

                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center">
                    <i className="ri-time-line text-orange-600"></i>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Đăng nhập cuối</p>
                    <p className="text-gray-900 font-medium">{user.lastLogin || 'Chưa đăng nhập'}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Permissions Card */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Quyền hạn và chức năng
        </h3>
        
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h4 className="text-sm font-medium text-blue-900 mb-3">
            Quyền hạn của {getRoleName(user.role)}:
          </h4>
          {user.role === 'manager' ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="flex items-center space-x-2 text-sm text-blue-800">
                <i className="ri-check-line text-green-600"></i>
                <span>Truy cập toàn bộ chức năng quản lý</span>
              </div>
              <div className="flex items-center space-x-2 text-sm text-blue-800">
                <i className="ri-check-line text-green-600"></i>
                <span>Quản lý sản phẩm và kho hàng</span>
              </div>
              <div className="flex items-center space-x-2 text-sm text-blue-800">
                <i className="ri-check-line text-green-600"></i>
                <span>Quản lý đơn hàng tất cả Partner</span>
              </div>
              <div className="flex items-center space-x-2 text-sm text-blue-800">
                <i className="ri-check-line text-green-600"></i>
                <span>Quản lý người dùng và phân quyền</span>
              </div>
              <div className="flex items-center space-x-2 text-sm text-blue-800">
                <i className="ri-check-line text-green-600"></i>
                <span>Xem báo cáo tài chính tổng quan</span>
              </div>
              <div className="flex items-center space-x-2 text-sm text-blue-800">
                <i className="ri-check-line text-green-600"></i>
                <span>Thiết lập tỷ lệ hoa hồng</span>
              </div>
              <div className="flex items-center space-x-2 text-sm text-green-800 font-medium">
                <i className="ri-check-line text-green-600"></i>
                <span>Tham gia bán hàng với hoa hồng {user.commissionRate}%</span>
              </div>
              <div className="flex items-center space-x-2 text-sm text-green-800">
                <i className="ri-check-line text-green-600"></i>
                <span>Xem cả giá sản xuất và giá nhập</span>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="flex items-center space-x-2 text-sm text-blue-800">
                <i className="ri-check-line text-green-600"></i>
                <span>Xem danh sách sản phẩm và tồn kho</span>
              </div>
              <div className="flex items-center space-x-2 text-sm text-blue-800">
                <i className="ri-check-line text-green-600"></i>
                <span>Tạo và quản lý đơn hàng cá nhân</span>
              </div>
              <div className="flex items-center space-x-2 text-sm text-blue-800">
                <i className="ri-check-line text-green-600"></i>
                <span>Xem thống kê doanh số cá nhân</span>
              </div>
              <div className="flex items-center space-x-2 text-sm text-blue-800">
                <i className="ri-check-line text-green-600"></i>
                <span>Xem hoa hồng và thu nhập</span>
              </div>
              <div className="flex items-center space-x-2 text-sm text-red-600">
                <i className="ri-close-line text-red-600"></i>
                <span>Không thể xem giá sản xuất</span>
              </div>
              <div className="flex items-center space-x-2 text-sm text-red-600">
                <i className="ri-close-line text-red-600"></i>
                <span>Không thể xem đơn hàng của người khác</span>
              </div>
            </div>
          )}
        </div>

        <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <h4 className="text-sm font-medium text-yellow-900 mb-2">
            Cách tính giá nhập cho {user.role === 'manager' ? 'Manager' : 'Partner'}:
          </h4>
          <p className="text-sm text-yellow-800">
            Giá nhập = Giá bán × (100% - {user.commissionRate}% hoa hồng)
          </p>
          <p className="text-xs text-yellow-700 mt-1">
            {user.role === 'manager' 
              ? 'Manager có thể xem cả giá sản xuất và giá nhập khi tham gia bán hàng'
              : 'Partner chỉ thấy giá nhập này thay vì giá sản xuất gốc'
            }
          </p>
        </div>
      </div>
    </div>
  );
}
